import { useMemo, useState, type ReactNode } from 'react';
import type {
  DemAnalyticsChannelTotals,
  DemAnalyticsHourlyChannelPoint,
  DemAnalyticsLeaderboardRow,
  DemAnalyticsMonthVolume,
  DemAnalyticsPersonCount,
  DemAnalyticsTrendPoint,
  DemAnalyticsUserKpi,
} from '../../types/dem';
import { formatProcessingDuration } from '../../lib/demProcessingTime';
import { hourlyChannelTotal } from '../../lib/demAnalytics';
import { formatCrmMonthYear } from '../../utils/dateTime';

type DemAnalyticsCardProps = {
  title: string;
  subtitle: string;
  children: ReactNode;
  accent?: 'blue' | 'green' | 'purple' | 'gold';
};

const CARD_ACCENTS = {
  blue: 'dem-analytics-card--blue',
  green: 'dem-analytics-card--green',
  purple: 'dem-analytics-card--purple',
  gold: 'dem-analytics-card--gold',
} as const;

export function DemAnalyticsCard({ title, subtitle, children, accent }: DemAnalyticsCardProps) {
  return (
    <section className={`card dem-analytics-card${accent ? ` ${CARD_ACCENTS[accent]}` : ''}`}>
      <header className="dem-analytics-card__head">
        <h3 className="dem-analytics-card__title">{title}</h3>
        <p className="dem-analytics-card__subtitle">{subtitle}</p>
      </header>
      <div className="dem-analytics-card__body">{children}</div>
    </section>
  );
}

type DemAnalyticsChannelSummaryProps = {
  totals: DemAnalyticsChannelTotals;
  showCancelled?: boolean;
};

export function DemAnalyticsChannelSummary({
  totals,
  showCancelled = false,
}: DemAnalyticsChannelSummaryProps) {
  const imported = totals.po + totals.manual + totals.b2b + totals.whatsapp;

  return (
    <div className="dem-analytics-channel-summary">
      <div className="dem-analytics-channel-stat dem-analytics-channel-stat--po">
        <span className="dem-analytics-channel-stat__label">PO</span>
        <strong className="dem-analytics-channel-stat__value">{totals.po.toLocaleString()}</strong>
        <span className="dem-analytics-channel-stat__hint">Desktop / email</span>
      </div>
      <div className="dem-analytics-channel-stat dem-analytics-channel-stat--manual">
        <span className="dem-analytics-channel-stat__label">Manual</span>
        <strong className="dem-analytics-channel-stat__value">{totals.manual.toLocaleString()}</strong>
        <span className="dem-analytics-channel-stat__hint">Manual order</span>
      </div>
      <div className="dem-analytics-channel-stat dem-analytics-channel-stat--b2b">
        <span className="dem-analytics-channel-stat__label">B2B</span>
        <strong className="dem-analytics-channel-stat__value">{totals.b2b.toLocaleString()}</strong>
        <span className="dem-analytics-channel-stat__hint">Web portal</span>
      </div>
      <div className="dem-analytics-channel-stat dem-analytics-channel-stat--wa">
        <span className="dem-analytics-channel-stat__label">WhatsApp</span>
        <strong className="dem-analytics-channel-stat__value">{totals.whatsapp.toLocaleString()}</strong>
        <span className="dem-analytics-channel-stat__hint">WhatsApp queue</span>
      </div>
      {showCancelled ? (
        <div className="dem-analytics-channel-stat dem-analytics-channel-stat--cancelled">
          <span className="dem-analytics-channel-stat__label">Cancelled</span>
          <strong className="dem-analytics-channel-stat__value">{totals.cancelled.toLocaleString()}</strong>
          <span className="dem-analytics-channel-stat__hint">Voided orders</span>
        </div>
      ) : (
        <div className="dem-analytics-channel-stat dem-analytics-channel-stat--total">
          <span className="dem-analytics-channel-stat__label">Imported total</span>
          <strong className="dem-analytics-channel-stat__value">{imported.toLocaleString()}</strong>
          <span className="dem-analytics-channel-stat__hint">All channels</span>
        </div>
      )}
    </div>
  );
}

type DemAnalyticsDeliveryStatsProps = {
  periodLabel: string;
  total: number;
  creators: DemAnalyticsPersonCount[];
  requestedBy: DemAnalyticsPersonCount[];
  issueCausedBy: DemAnalyticsPersonCount[];
  monthVolumes?: DemAnalyticsMonthVolume[];
  selectedYearMonth?: string;
  onSelectYearMonth?: (yearMonth: string) => void;
};

function DeliveryPersonBarChart({
  title,
  rows,
  tone,
  emptyLabel,
}: {
  title: string;
  rows: DemAnalyticsPersonCount[];
  tone: 'creator' | 'requested' | 'issue';
  emptyLabel: string;
}) {
  const max = rows[0]?.total || 1;
  const covered = rows.reduce((sum, row) => sum + row.total, 0);
  return (
    <section className={`delivery-stats__section delivery-stats__section--${tone}`}>
      <div className="delivery-stats__section-head">
        <h3 className="delivery-stats__section-title">{title}</h3>
        <p className="delivery-stats__section-meta">
          {rows.length === 0
            ? '0 entries'
            : `${rows.length} ${rows.length === 1 ? 'entry' : 'entries'} · ${covered} total`}
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="delivery-stats__empty">{emptyLabel}</p>
      ) : (
        <div className="delivery-stats__chart" role="img" aria-label={`${title} bar chart`}>
          <div className="delivery-stats__chart-plot" aria-hidden>
            {rows.map((row) => {
              const pct = Math.max(10, Math.round((row.total / max) * 100));
              return (
                <div key={`${title}-${row.person}`} className="delivery-stats__col">
                  <span className="delivery-stats__col-value">{row.total}</span>
                  <div className="delivery-stats__col-track">
                    <div className="delivery-stats__col-fill" style={{ height: `${pct}%` }} />
                  </div>
                  <span className="delivery-stats__col-label" title={row.person}>
                    {row.person}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="delivery-stats__legend" role="list" aria-label={`${title} counts`}>
            {rows.map((row) => (
              <div key={`${title}-legend-${row.person}`} className="delivery-stats__legend-row" role="listitem">
                <span className="delivery-stats__person">{row.person}</span>
                <span className="delivery-stats__num">{row.total}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function DemAnalyticsDeliveryStats({
  periodLabel,
  total,
  creators,
  requestedBy,
  issueCausedBy,
  monthVolumes,
  selectedYearMonth,
  onSelectYearMonth,
}: DemAnalyticsDeliveryStatsProps) {
  const volumeMax = Math.max(1, ...(monthVolumes ?? []).map((m) => m.total));

  return (
    <div className="delivery-stats dem-analytics-embedded-stats">
      <p className="delivery-stats__summary">
        Showing <strong>{total}</strong> arrangement{total === 1 ? '' : 's'} for{' '}
        <strong>{periodLabel}</strong>
      </p>

      {monthVolumes && monthVolumes.length > 0 && selectedYearMonth && onSelectYearMonth ? (
        <section className="delivery-stats__section delivery-stats__section--volume">
          <div className="delivery-stats__section-head">
            <h3 className="delivery-stats__section-title">Monthly volume</h3>
            <p className="delivery-stats__section-meta">Click a month to filter the graphs below</p>
          </div>
          <div className="delivery-stats__volume" role="list" aria-label="Arrangements by month">
            {monthVolumes.map((month) => {
              const pct = Math.max(8, Math.round((month.total / volumeMax) * 100));
              const active = month.yearMonth === selectedYearMonth;
              return (
                <button
                  key={month.yearMonth}
                  type="button"
                  role="listitem"
                  className={`delivery-stats__volume-col${active ? ' is-active' : ''}`}
                  onClick={() => onSelectYearMonth(month.yearMonth)}
                  aria-pressed={active}
                  title={`${formatCrmMonthYear(month.yearMonth)}: ${month.total}`}
                >
                  <span className="delivery-stats__volume-value">{month.total}</span>
                  <span className="delivery-stats__volume-track" aria-hidden>
                    <span className="delivery-stats__volume-fill" style={{ height: `${pct}%` }} />
                  </span>
                  <span className="delivery-stats__volume-label">
                    {formatCrmMonthYear(month.yearMonth)}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {total === 0 ? (
        <p className="delivery-stats__empty">No arrangements for this period.</p>
      ) : (
        <div className="delivery-stats__grid">
          <DeliveryPersonBarChart
            title="Creator"
            rows={creators}
            tone="creator"
            emptyLabel="No creator data for this period."
          />
          <DeliveryPersonBarChart
            title="Requested by"
            rows={requestedBy}
            tone="requested"
            emptyLabel="No requested-by data for this period."
          />
          <DeliveryPersonBarChart
            title="Issue caused by"
            rows={issueCausedBy}
            tone="issue"
            emptyLabel="No issue-caused-by data for this period."
          />
        </div>
      )}
    </div>
  );
}

type DemAnalyticsCffStatsProps = {
  periodLabel: string;
  total: number;
  persons: DemAnalyticsPersonCount[];
};

export function DemAnalyticsCffStats({ periodLabel, total, persons }: DemAnalyticsCffStatsProps) {
  const max = persons[0]?.total || 1;
  return (
    <div className="cff-stats dem-analytics-embedded-stats">
      <p className="cff-stats__summary">
        <strong>{total}</strong> CFF{total === 1 ? '' : 's'} in {periodLabel} ·{' '}
        <strong>{persons.length}</strong> person{persons.length === 1 ? '' : 's'}
      </p>
      {persons.length === 0 ? (
        <p className="cff-stats__empty">No CFF forms for this period.</p>
      ) : (
        <div className="cff-stats__visual" role="list" aria-label="CFF counts by creator">
          {persons.map((row) => {
            const pct = Math.max(6, Math.round((row.total / max) * 100));
            return (
              <div key={row.person} className="cff-stats__bar-row" role="listitem">
                <div className="cff-stats__bar-meta">
                  <span className="cff-stats__person">{row.person}</span>
                  <span className="cff-stats__num cff-stats__num--total">{row.total}</span>
                </div>
                <div className="cff-stats__bar-track" aria-hidden>
                  <div className="cff-stats__bar-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type DemAnalyticsTrendChartProps = {
  data: DemAnalyticsTrendPoint[];
  formatLabel: (label: string) => string;
};

export function DemAnalyticsTrendChart({ data, formatLabel }: DemAnalyticsTrendChartProps) {
  const max = Math.max(1, ...data.flatMap((d) => [d.processed, d.cancelled]));

  if (data.every((d) => d.processed === 0 && d.cancelled === 0)) {
    return <p className="dem-analytics-empty">No trend data for this period.</p>;
  }

  return (
    <div className="dem-analytics-trend">
      <div className="dem-analytics-trend__legend">
        <span className="dem-analytics-legend dem-analytics-legend--processed">Imported</span>
        <span className="dem-analytics-legend dem-analytics-legend--cancelled">Cancelled</span>
      </div>
      <div className="dem-analytics-trend__plot" role="img" aria-label="Order trend chart">
        {data.map((point) => (
          <div key={point.label} className="dem-analytics-trend__col">
            <div className="dem-analytics-trend__values">
              {point.processed > 0 ? (
                <span className="dem-analytics-trend__value dem-analytics-trend__value--processed">
                  {point.processed}
                </span>
              ) : null}
              {point.cancelled > 0 ? (
                <span className="dem-analytics-trend__value dem-analytics-trend__value--cancelled">
                  {point.cancelled}
                </span>
              ) : null}
            </div>
            <div className="dem-analytics-trend__bars">
              <div
                className="dem-analytics-trend__bar dem-analytics-trend__bar--processed"
                style={{
                  height:
                    point.processed > 0
                      ? `${Math.max(4, (point.processed / max) * 100)}%`
                      : '0',
                }}
              />
              <div
                className="dem-analytics-trend__bar dem-analytics-trend__bar--cancelled"
                style={{
                  height:
                    point.cancelled > 0
                      ? `${Math.max(4, (point.cancelled / max) * 100)}%`
                      : '0',
                }}
              />
            </div>
            <span className="dem-analytics-trend__label">{formatLabel(point.label)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const HOURLY_CHANNELS = [
  { key: 'po', label: 'PO', className: 'dem-analytics-hourly__seg--po' },
  { key: 'manual', label: 'Manual', className: 'dem-analytics-hourly__seg--manual' },
  { key: 'b2b', label: 'B2B', className: 'dem-analytics-hourly__seg--b2b' },
  { key: 'whatsapp', label: 'WhatsApp', className: 'dem-analytics-hourly__seg--whatsapp' },
] as const;

/** Work = 08:00–18:00 (8am–6pm); Rest = 00:00–07:00 + 19:00–23:00. */
type HourlyBand = 'work' | 'rest';

function hourFromLabel(label: string): number {
  const hour = Number(label);
  return Number.isFinite(hour) ? hour : -1;
}

function isWorkHour(hour: number): boolean {
  return hour >= 8 && hour <= 18;
}

function formatHourLabel(label: string): string {
  const hour = hourFromLabel(label);
  if (hour < 0) return `${label}:00`;
  return `${String(hour).padStart(2, '0')}:00`;
}

type DemAnalyticsHourlyChannelChartProps = {
  data: DemAnalyticsHourlyChannelPoint[];
};

export function DemAnalyticsHourlyChannelChart({ data }: DemAnalyticsHourlyChannelChartProps) {
  const [band, setBand] = useState<HourlyBand>('work');

  const visible = useMemo(() => {
    const filtered = data.filter((point) => {
      const hour = hourFromLabel(point.label);
      return band === 'work' ? isWorkHour(hour) : !isWorkHour(hour);
    });
    // Rest: show overnight hours in clock order (19→23, then 00→07).
    if (band === 'rest') {
      return [...filtered].sort((a, b) => {
        const ah = hourFromLabel(a.label);
        const bh = hourFromLabel(b.label);
        const aKey = ah < 8 ? ah + 24 : ah;
        const bKey = bh < 8 ? bh + 24 : bh;
        return aKey - bKey;
      });
    }
    return filtered;
  }, [band, data]);

  const max = Math.max(1, ...visible.map((d) => hourlyChannelTotal(d)));
  const bandTotal = visible.reduce((sum, point) => sum + hourlyChannelTotal(point), 0);
  const hasAny = data.some((d) => hourlyChannelTotal(d) > 0);

  if (!hasAny) {
    return <p className="dem-analytics-empty">No orders for this period.</p>;
  }

  return (
    <div className="dem-analytics-trend dem-analytics-trend--hourly">
      <div className="dem-analytics-hourly__toolbar">
        <div className="dem-analytics-hourly__bands" role="tablist" aria-label="Hour band">
          <button
            type="button"
            role="tab"
            aria-selected={band === 'work'}
            className={`dem-filter-btn${band === 'work' ? ' dem-filter-btn--active' : ''}`}
            onClick={() => setBand('work')}
          >
            Work 08:00–18:00
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={band === 'rest'}
            className={`dem-filter-btn${band === 'rest' ? ' dem-filter-btn--active' : ''}`}
            onClick={() => setBand('rest')}
          >
            Rest hours
          </button>
        </div>
        <p className="dem-analytics-hourly__band-note">
          {band === 'work'
            ? `${bandTotal.toLocaleString()} order${bandTotal === 1 ? '' : 's'} · 08:00–18:00`
            : `${bandTotal.toLocaleString()} order${bandTotal === 1 ? '' : 's'} · 19:00–07:00`}
        </p>
      </div>

      <div className="dem-analytics-trend__legend">
        <span className="dem-analytics-legend dem-analytics-legend--po">PO</span>
        <span className="dem-analytics-legend dem-analytics-legend--manual">Manual</span>
        <span className="dem-analytics-legend dem-analytics-legend--b2b">B2B</span>
        <span className="dem-analytics-legend dem-analytics-legend--whatsapp">WhatsApp</span>
      </div>

      {bandTotal === 0 ? (
        <p className="dem-analytics-empty">
          {band === 'work'
            ? 'No orders during work hours (08:00–18:00).'
            : 'No orders during rest hours.'}
        </p>
      ) : (
        <div
          className="dem-analytics-trend__plot dem-analytics-trend__plot--hourly-band"
          role="img"
          aria-label={
            band === 'work'
              ? 'Orders by work hour and channel'
              : 'Orders by rest hour and channel'
          }
        >
          {visible.map((point) => {
            const total = hourlyChannelTotal(point);
            const tip = [
              formatHourLabel(point.label),
              total > 0 ? `${total} total` : 'no orders',
              point.po > 0 ? `PO ${point.po}` : null,
              point.manual > 0 ? `Manual ${point.manual}` : null,
              point.b2b > 0 ? `B2B ${point.b2b}` : null,
              point.whatsapp > 0 ? `WhatsApp ${point.whatsapp}` : null,
            ]
              .filter(Boolean)
              .join(' · ');

            return (
              <div key={point.label} className="dem-analytics-trend__col" title={tip}>
                <div className="dem-analytics-trend__values">
                  {total > 0 ? (
                    <span className="dem-analytics-trend__value dem-analytics-trend__value--processed">
                      {total}
                    </span>
                  ) : (
                    <span className="dem-analytics-trend__value dem-analytics-trend__value--empty">
                      ·
                    </span>
                  )}
                </div>
                <div className="dem-analytics-hourly__bars">
                  <div
                    className="dem-analytics-hourly__stack"
                    style={{
                      height: total > 0 ? `${Math.max(10, (total / max) * 100)}%` : '0',
                    }}
                  >
                    {HOURLY_CHANNELS.map(({ key, label, className }) => {
                      const value = point[key];
                      if (value <= 0 || total <= 0) return null;
                      return (
                        <div
                          key={key}
                          className={`dem-analytics-hourly__seg ${className}`}
                          style={{ flexGrow: value, flexBasis: 0 }}
                          title={`${label}: ${value}`}
                        />
                      );
                    })}
                  </div>
                </div>
                <span className="dem-analytics-trend__label">{formatHourLabel(point.label)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type DemAnalyticsUserChannelChartProps = {
  data: DemAnalyticsUserKpi[];
  showCancelled: boolean;
};

function userRowTotal(row: DemAnalyticsUserKpi, showCancelled: boolean): number {
  return row.po + row.manual + row.b2b + row.whatsapp + (showCancelled ? row.cancelled : 0);
}

export function DemAnalyticsUserChannelChart({ data, showCancelled }: DemAnalyticsUserChannelChartProps) {
  if (data.length === 0) {
    return <p className="dem-analytics-empty">No activity for this period.</p>;
  }

  const max = Math.max(1, ...data.map((row) => userRowTotal(row, showCancelled)));

  return (
    <div className="dem-analytics-user-channel">
      <div className="dem-analytics-trend__legend">
        <span className="dem-analytics-legend dem-analytics-legend--po">PO</span>
        <span className="dem-analytics-legend dem-analytics-legend--manual">Manual</span>
        <span className="dem-analytics-legend dem-analytics-legend--b2b">B2B</span>
        <span className="dem-analytics-legend dem-analytics-legend--whatsapp">WhatsApp</span>
        {showCancelled ? (
          <span className="dem-analytics-legend dem-analytics-legend--cancelled">Cancelled</span>
        ) : null}
      </div>
      <div className="dem-analytics-user-channel__list">
        {data.map((row) => {
          const total = userRowTotal(row, showCancelled);
          const scale = (value: number) => `${(value / max) * 100}%`;

          return (
            <div key={row.user} className="dem-analytics-user-channel__row">
              <div className="dem-analytics-user-channel__meta">
                <span className="dem-analytics-user-channel__name" title={row.user}>
                  {row.user}
                </span>
                <span className="dem-analytics-user-channel__total">{total}</span>
              </div>
              <div className="dem-analytics-user-channel__track">
                {row.po > 0 ? (
                  <div
                    className="dem-analytics-user-channel__seg dem-analytics-user-channel__seg--po"
                    style={{ width: scale(row.po) }}
                    title={`PO: ${row.po}`}
                  >
                    {row.po > 0 && total <= 12 ? null : row.po > 0 ? <span>{row.po}</span> : null}
                  </div>
                ) : null}
                {row.manual > 0 ? (
                  <div
                    className="dem-analytics-user-channel__seg dem-analytics-user-channel__seg--manual"
                    style={{ width: scale(row.manual) }}
                    title={`Manual: ${row.manual}`}
                  >
                    {row.manual > 0 && total <= 12 ? null : row.manual > 0 ? <span>{row.manual}</span> : null}
                  </div>
                ) : null}
                {row.b2b > 0 ? (
                  <div
                    className="dem-analytics-user-channel__seg dem-analytics-user-channel__seg--b2b"
                    style={{ width: scale(row.b2b) }}
                    title={`B2B: ${row.b2b}`}
                  >
                    {row.b2b > 0 && total <= 12 ? null : row.b2b > 0 ? <span>{row.b2b}</span> : null}
                  </div>
                ) : null}
                {row.whatsapp > 0 ? (
                  <div
                    className="dem-analytics-user-channel__seg dem-analytics-user-channel__seg--whatsapp"
                    style={{ width: scale(row.whatsapp) }}
                    title={`WhatsApp: ${row.whatsapp}`}
                  >
                    {row.whatsapp > 0 && total <= 12 ? null : row.whatsapp > 0 ? <span>{row.whatsapp}</span> : null}
                  </div>
                ) : null}
                {showCancelled && row.cancelled > 0 ? (
                  <div
                    className="dem-analytics-user-channel__seg dem-analytics-user-channel__seg--cancelled"
                    style={{ width: scale(row.cancelled) }}
                    title={`Cancelled: ${row.cancelled}`}
                  >
                    <span>{row.cancelled}</span>
                  </div>
                ) : null}
              </div>
              <div className="dem-analytics-user-channel__breakdown">
                {row.po > 0 ? <span className="dem-analytics-user-channel__chip dem-analytics-user-channel__chip--po">PO {row.po}</span> : null}
                {row.manual > 0 ? (
                  <span className="dem-analytics-user-channel__chip dem-analytics-user-channel__chip--manual">
                    Manual {row.manual}
                  </span>
                ) : null}
                {row.b2b > 0 ? <span className="dem-analytics-user-channel__chip dem-analytics-user-channel__chip--b2b">B2B {row.b2b}</span> : null}
                {row.whatsapp > 0 ? (
                  <span className="dem-analytics-user-channel__chip dem-analytics-user-channel__chip--whatsapp">WA {row.whatsapp}</span>
                ) : null}
                {showCancelled && row.cancelled > 0 ? (
                  <span className="dem-analytics-user-channel__chip dem-analytics-user-channel__chip--cancelled">
                    Cancelled {row.cancelled}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

type DemAnalyticsYearBarChartProps = {
  data: { year: number; count: number }[];
};

export function DemAnalyticsYearBarChart({ data }: DemAnalyticsYearBarChartProps) {
  if (data.length === 0) {
    return <p className="dem-analytics-empty">No imported orders yet.</p>;
  }

  const max = Math.max(1, ...data.map((d) => d.count));

  return (
    <div className="dem-analytics-yoy">
      {data.map((row) => (
        <div key={row.year} className="dem-analytics-yoy__row">
          <span className="dem-analytics-yoy__year">{row.year}</span>
          <div className="dem-analytics-yoy__track">
            <div className="dem-analytics-yoy__bar" style={{ width: `${(row.count / max) * 100}%` }}>
              <span>{row.count}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

type DemAnalyticsLeaderboardChartProps = {
  data: DemAnalyticsLeaderboardRow[];
};

export function DemAnalyticsLeaderboardChart({ data }: DemAnalyticsLeaderboardChartProps) {
  if (data.length === 0) {
    return <p className="dem-analytics-empty">No imports in the selected year.</p>;
  }

  const max = Math.max(1, ...data.map((d) => d.count));

  return (
    <div className="dem-analytics-leaderboard">
      <div className="dem-analytics-trend__legend">
        <span className="dem-analytics-legend dem-analytics-legend--po">PO</span>
        <span className="dem-analytics-legend dem-analytics-legend--manual">Manual</span>
        <span className="dem-analytics-legend dem-analytics-legend--b2b">B2B</span>
        <span className="dem-analytics-legend dem-analytics-legend--whatsapp">WhatsApp</span>
      </div>
      {data.map((row, index) => (
        <div key={row.user} className="dem-analytics-leaderboard__row">
          <div className="dem-analytics-leaderboard__meta">
            <span className="dem-analytics-leaderboard__rank">#{index + 1}</span>
            <span className="dem-analytics-leaderboard__name" title={row.user}>
              {row.user}
            </span>
            <span className="dem-analytics-leaderboard__count">{row.count}</span>
          </div>
          <div className="dem-analytics-leaderboard__track">
            {row.po > 0 ? (
              <div
                className="dem-analytics-leaderboard__seg dem-analytics-leaderboard__seg--po"
                style={{ width: `${(row.po / max) * 100}%` }}
                title={`PO: ${row.po}`}
              />
            ) : null}
            {row.manual > 0 ? (
              <div
                className="dem-analytics-leaderboard__seg dem-analytics-leaderboard__seg--manual"
                style={{ width: `${(row.manual / max) * 100}%` }}
                title={`Manual: ${row.manual}`}
              />
            ) : null}
            {row.b2b > 0 ? (
              <div
                className="dem-analytics-leaderboard__seg dem-analytics-leaderboard__seg--b2b"
                style={{ width: `${(row.b2b / max) * 100}%` }}
                title={`B2B: ${row.b2b}`}
              />
            ) : null}
            {row.whatsapp > 0 ? (
              <div
                className="dem-analytics-leaderboard__seg dem-analytics-leaderboard__seg--whatsapp"
                style={{ width: `${(row.whatsapp / max) * 100}%` }}
                title={`WhatsApp: ${row.whatsapp}`}
              />
            ) : null}
          </div>
          <div className="dem-analytics-user-channel__breakdown">
            {row.po > 0 ? <span className="dem-analytics-user-channel__chip dem-analytics-user-channel__chip--po">PO {row.po}</span> : null}
            {row.manual > 0 ? (
              <span className="dem-analytics-user-channel__chip dem-analytics-user-channel__chip--manual">
                Manual {row.manual}
              </span>
            ) : null}
            {row.b2b > 0 ? <span className="dem-analytics-user-channel__chip dem-analytics-user-channel__chip--b2b">B2B {row.b2b}</span> : null}
            {row.whatsapp > 0 ? (
              <span className="dem-analytics-user-channel__chip dem-analytics-user-channel__chip--whatsapp">WA {row.whatsapp}</span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

type DemAnalyticsDonutProps = {
  slices: { label: string; count: number; color: string }[];
};

export function DemAnalyticsUserEfficiencyTable({ data }: { data: DemAnalyticsUserKpi[] }) {
  const rows = [...data]
    .filter((row) => row.processingSeconds > 0 && row.timedOrders > 0)
    .map((row) => ({
      ...row,
      avgSec: Math.round(row.processingSeconds / row.timedOrders),
    }))
    .sort((a, b) => b.processingSeconds - a.processingSeconds || a.avgSec - b.avgSec);

  if (rows.length === 0) {
    return <p className="dem-analytics-empty">No processing time recorded for this period.</p>;
  }

  return (
    <div className="dem-analytics-efficiency">
      <div className="dem-analytics-efficiency__head" aria-hidden>
        <span>User</span>
        <span>Timed orders</span>
        <span>Total time</span>
        <span>Avg / order</span>
      </div>
      <ul className="dem-analytics-efficiency__list">
        {rows.map((row) => (
          <li key={row.user} className="dem-analytics-efficiency__row">
            <span className="dem-analytics-efficiency__user" title={row.user}>
              {row.user}
            </span>
            <span className="dem-analytics-efficiency__orders">
              {row.timedOrders.toLocaleString()}
            </span>
            <span className="dem-analytics-efficiency__total">
              {formatProcessingDuration(row.processingSeconds)}
            </span>
            <span className="dem-analytics-efficiency__avg">
              {formatProcessingDuration(row.avgSec)}
            </span>
          </li>
        ))}
      </ul>
      <p className="dem-analytics-efficiency__hint">
        Average uses only orders with recorded processing time. Lower average is faster.
      </p>
    </div>
  );
}

export function DemAnalyticsDonut({ slices }: DemAnalyticsDonutProps) {
  const total = slices.reduce((sum, slice) => sum + slice.count, 0);
  if (total === 0) {
    return <p className="dem-analytics-empty">No data for this year.</p>;
  }

  const imported = slices.find((s) => s.label === 'Imported')?.count ?? 0;
  const pct = (imported / total) * 100;

  let cursor = 0;
  const gradient = slices
    .map((slice) => {
      const start = (cursor / total) * 100;
      cursor += slice.count;
      const end = (cursor / total) * 100;
      return `${slice.color} ${start}% ${end}%`;
    })
    .join(', ');

  return (
    <div className="dem-analytics-donut-wrap">
      <div className="dem-analytics-donut" style={{ background: `conic-gradient(${gradient})` }}>
        <div className="dem-analytics-donut__center">
          <strong>{pct.toFixed(1)}%</strong>
          <span>Imported</span>
        </div>
      </div>
      <ul className="dem-analytics-donut__legend">
        {slices.map((slice) => (
          <li key={slice.label}>
            <span className="dem-analytics-donut__swatch" style={{ background: slice.color }} />
            {slice.label}
            <strong>{slice.count}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}
